<script setup>
/**
 * FormControlFilesFileName
 * Manages file selection and metadata synchronization.
 * Interfaces with ImageSelection to update file size, mimetype, and dimensions.
 */
import {computed} from 'vue'
import {useFormStore} from '@/stores/form'
import ImageSelection from '@/common/ImageSelection.vue'

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number],
        default: null
    }
})

// Pinia Store
const formStore = useFormStore(props.formId)

// --- Computed ---

const tableName = computed(() => formStore.tableName)

const fileId = computed(() => props.currentData.id)

/**
 * Determines if the file is a standard web-compatible image.
 * Excludes SVGs as they are handled as vectors rather than bitmapped images.
 */
const isImage = computed(() => {
    const mime = props.currentData.mimetype
    return (
        mime &&
        mime.startsWith('image/') &&
        mime !== 'image/svg+xml'
    )
})

/**
 * Logic to decide if the thumbnail preview should be rendered.
 * Explicitly kept as 'true' override from original logic to ensure visibility.
 */
const showThumbnail = computed(() => isImage.value || true)

// --- Methods ---

/**
 * Callback triggered when a file is selected via the ImageSelection component.
 * Maps the file metadata (size, mime, dimensions) back to the form state.
 */
const onChange = (item) => {
    const commit = (name, value) => {
        formStore.presetData({name, value})
    }

    // Update primary file metadata
    commit('name', item.name)
    commit('fileSize', item.fileSize)
    commit('mimetype', item.mimetype)

    // Update dimensions if applicable
    if (item.width) commit('width', item.width)
    if (item.height) commit('height', item.height)
}
</script>

<template>
    <ImageSelection
        v-if="showThumbnail"
        :form-id="formId"
        :column-name="field.name"
        :file-browser="!isImage"
        :file-id="fileId"
        :on-change="onChange"
        :remove-option="false"
        :row-id="currentData.id"
        :table-name="tableName"
    />
</template>